<template>
    <v-app>
        <v-overlay v-model="baseStore.isLoading" class="loading-overlay">
            <v-progress-circular indeterminate size="64" />
        </v-overlay>
        <notifications-popup ref="notifications" />
        <v-main v-if="initialized">
            <div class="mpc">
                <v-alert v-if="!baseStore.isOnline" class="caption mb-1" color="rgba(239, 0, 0, 0.1)" dense>
                    <span class="font-weight-bold">Offline modus.</span>
                    Dataene lagres kun lokalt inntil du kobles til nett.
                </v-alert>
                <v-container class="subtitle-2 px-0">
                    <v-tabs v-model="menu" data-id="menu">
                        <v-tab
                            :to="{
                                name: 'home',
                                query: {
                                    token: router.currentRoute.value.query.token
                                }
                            }"
                            value="lines"
                        >
                            Ruter
                        </v-tab>
                        <v-tab
                            v-if="baseStore.departuresPage"
                            :to="{
                                name: baseStore.departuresPage.urlName,
                                params: {
                                    lineNumber: baseStore.lineNumber
                                },
                                query: {
                                    token: router.currentRoute.value.query.token
                                }
                            }"
                            value="departures"
                        >
                            {{ baseStore.departuresPage.pageName }}
                        </v-tab>
                        <v-tab v-if="baseStore.tripPage" value="trip">
                            {{ baseStore.tripPage.pageName }}
                        </v-tab>
                        <v-tab
                            :to="{
                                name: 'bookingLog',
                                query: {
                                    token: router.currentRoute.value.query.token
                                }
                            }"
                            value="bookingLog"
                        >
                            Logg
                        </v-tab>
                        <v-tab
                            v-if="showMpcStatistics"
                            :to="{
                                name: 'mpcStatisticsDaily',
                                query: {
                                    token: router.currentRoute.value.query.token
                                }
                            }"
                            value="stats"
                        >
                            MPC statistikk
                        </v-tab>
                    </v-tabs>
                    <v-divider />
                </v-container>
                <router-view />
            </div>
        </v-main>
        <v-snackbar v-model="showUpdateDialog" :timeout="-1">
            En ny versjon av appen ble funnet.
            <v-btn color="white" variant="text" @click="reload"> Oppdater</v-btn>
        </v-snackbar>
    </v-app>
</template>

<script lang="ts" setup>
import NotificationsPopup from '@/features/shared/NotificationsPopup.vue';
import { equalDates } from '@/helpers/DateHelpers';
import { poll } from '@/helpers/Polling';
import { isSyncInProgress } from '@/helpers/OfflineHelpers';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Events, useEmitter } from '@/plugins/emitter';
import { useBaseStore } from './features/shared/baseStore';
import { useLinesStore } from './features/lines/linesStore';
import { useSettingsStore } from './features/shared/settingsStore';

const baseStore = useBaseStore();
const settingsStore = useSettingsStore();
const linesStore = useLinesStore();
const showUpdateDialog = ref(false);
const menu = ref(null);
const initialized = ref(false);
const notifications = ref<InstanceType<typeof NotificationsPopup>>();
const route = useRoute();
const router = useRouter();
const eventbus = useEmitter();

initialize();

const selectedDate = computed(() => {
    return baseStore.selectedDate;
});

const showMpcStatistics = computed(() => {
    return settingsStore.settings && settingsStore.showMpcStatistics;
});

watch(
    () => route.query,
    async (query: any) => {
        const date = getRouteDate(query);
        setDate(date);
    }
);

watch(
    () => baseStore.isLoading,
    isLoading => {
        if (!isLoading) setMenuTab(route.name);
    }
);

async function initialize() {
    await registerNetworkEvents();
    eventbus.emitter.on(Events.serviceWorkerUpdated, () => (showUpdateDialog.value = true));
    eventbus.emitter.on(Events.notification, e => notifications.value.show(e as string));
    eventbus.emitter.on(Events.goBack, () => router.go(-1));

    const date = getRouteDate(route.query);
    setDate(date);

    await loadData();

    initialized.value = true;
    eventbus.emitter.emit(Events.initialized);
    eventbus.emitter.on(Events.menuUpdate, e => setMenuTab(e));
    eventbus.emitter.on(Events.dateChanged, () => linesStore.fetchLines(baseStore.calendarDay));
}

async function loadData() {
    if (!settingsStore.settings) {
        await settingsStore.fetchSettings();
        updateTitle();
    }

    if (linesStore.lines.length < 1) {
        await linesStore.fetchLines(baseStore.calendarDay);
    }
}

function reload() {
    eventbus.emitter.emit(Events.updateServiceWorker);
}

function setDate(date: Date) {
    if (equalDates(selectedDate.value, date)) return;
    baseStore.selectedDate = date;

    eventbus.emitter.emit(Events.dateChanged);
}

function setMenuTab(tab) {
    menu.value = tab;
}

function getRouteDate(query: any) {
    return query.date ? new Date(Date.parse(String(query.date))) : new Date();
}

async function registerNetworkEvents() {
    const connection = getSignalRConnection();
    connection.onreconnected(() => setOnlineSync());
    connection.onreconnecting(() => setOfflineSync());
    await connection.start();

    // set up online/offline listeners
    window.addEventListener('online', () => setOnlineSync());
    window.addEventListener('offline', () => setOfflineSync());

    // set initial online state
    baseStore.isOnline = navigator.onLine;
}

function setOnlineSync() {
    (async () => {
        await setOnline();
    })();
}

function setOfflineSync() {
    (async () => {
        await setOffline();
    })();
}

async function setOnline() {
    if (baseStore.isOnline) return; // Already online

    eventbus.emitter.emit(Events.online);

    await poll({
        fn: isSyncInProgress,
        validate: (isSyncing: any) => !isSyncing,
        interval: 500
    });
    baseStore.isOnline = true;
}

async function setOffline() {
    if (!baseStore.isOnline) return; // Already offline

    baseStore.isOnline = false;
}

function getSignalRConnection() {
    return new HubConnectionBuilder()
        .withUrl('/online-status')
        .withAutomaticReconnect({
            nextRetryDelayInMilliseconds: retryContext => {
                if (retryContext.elapsedMilliseconds < 30000) {
                    // If we've been reconnecting for less than 30 seconds so far,
                    // wait 1, 2, 3, 4 ... seconds
                    return retryContext.previousRetryCount * 1000;
                } else {
                    // If we've been reconnecting for more than 30 seconds so far, try reconnecting each 10 seconds.
                    return 10000;
                }
            }
        })
        .configureLogging(LogLevel.Information)
        .build();
}

function updateTitle() {
    const companyName = settingsStore.settings.companyName;
    document.title = `Manuell passasjertelling ${companyName}`;
}
</script>

<style scoped>
#app {
    overflow-x: hidden;
}

.mpc {
    max-width: 1024px;
    margin: 0 auto;
}
</style>

<style lang="scss">
.gradient-bg {
    background: linear-gradient(rgba(255, 255, 255, 0), rgba(255, 255, 255, 1) 20%);
}

.nav-link {
    text-decoration: none;
    height: 100%;
    display: flex;
    align-content: center;
    align-items: center;
    color: inherit;
}

.selection-tabs {
    .v-tab {
        background: rgba(0, 0, 0, 0.03);

        &--active,
        &--selected {
            background: rgba(0, 0, 0, 0.06);
        }
    }
}

.loading-overlay .v-overlay__content {
    right: calc(50% - 32px);
    top: calc(50% - 32px);
}

.subtitle-2 {
    font-size: 0.875rem !important;
    font-weight: 500;
}

@media (hover: none) and (pointer: coarse) {
    .v-btn:hover > .v-btn__overlay {
        opacity: 0;
    }
}

// Unselected v-select dropdown items should have no background tint.
.v-select__content .v-list-item:not(.v-list-item--active) {
    background-color: transparent;

    .v-list-item__overlay {
        opacity: 0;
    }
}
</style>
@/services/emitter
